import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import moment from 'moment'

import { SmoothTransition } from 'components/pageTransition'
import { PagePreloader } from 'components/pagePreloader'
import { CustomTable } from 'components/customTable'
import { RenderDefaultActions, RenderStatus }
  from '../../common'

import { usePopup } from 'context/popupContext'
import { useNotificationBar } from 'context/notificationContext'
import {
  parseSearchParams,
  getUpdatedSearchParams,
  getTimeRelativeTime,
  getErrorResponse,
} from 'services/util'

import {
  useGetTemplatesTypesData,
  useUpdateTemplatesTypeData,
  useDeleteTemplatesTypeData,
} from '../../../../hooks/templatesType'

import { AddUpdate } from './addUpdateTemplateType'
import { Unauthorized } from '../../../Unauthorized'
import { twElementStyles } from 'twElementStyles'

const TemplateTypeMaster = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams = parseSearchParams(searchParams, 'filter')

  const popup = usePopup()
  const notification = useNotificationBar()

  const [addUpdate, setAddUpdate] = useState<boolean>(false)
  const [addUpdateInput, setAddUpdateInput] = useState<any>(null)
  const [editForm, setEditForm] = useState<boolean>(false)

  const {
    data,
    isFetching,
    isError,
    isSuccess,
    error,
    refetch,
    dataUpdatedAt,
  } = useGetTemplatesTypesData(filterParams)

  const { mutate: updateTemplateType } = useUpdateTemplatesTypeData()
  const { mutate: deleteTemplateType } = useDeleteTemplatesTypeData()





  const responseData = data?.data

  const normalizedResults = responseData?.results?.map((item: any) => {
    const finalId = item.template_type_id ?? item.id

    return {
      ...item,

      // 🔥 ALWAYS SET BOTH IDS
      id: finalId,
      template_type_id: finalId,

      // 🔥 NORMALIZE CODE FIELDS (THIS IS THE MAIN FIX)
      code: item.code || item.template_code || item.template_type,          // ✅ for Code field
      widget_code: item.widget_code || item.template_type,

      // 🔥 FULL COMPATIBILITY FIELDS
      template_code: item.template_code || item.code,
      template_type: item.template_type || item.code,
      template_type_name: item.template_type_name || item.name,
    }
  })



  /* ---------------- Status Change ---------------- */
  const onStatusChange = (row: any) => {
    const updateValue = { status: row.status ? 0 : 1 }

    popup.open({
      title: `${updateValue.status ? 'Activate' : 'Deactivate'} Template Type?`,
      message: `Do you want to ${updateValue.status ? 'activate' : 'deactivate'
        } ${row?.name}?`,
      type: 'confirm',
      onAction: (confirm: boolean) => {
        if (confirm) {
          updateTemplateType(
            { id: row.id, data: updateValue },
            {
              onSuccess: () => {
                notification.push({
                  title: 'Success!',
                  message: `Template type ${updateValue.status ? 'activated' : 'deactivated'
                    } successfully.`,
                  type: updateValue.status ? 'success' : 'info',
                })
                refetch()
              },
              onError: (err) => notification.push(getErrorResponse(err)),
            }
          )

        }
      },
    })
  }

  /* ---------------- Delete ---------------- */
  const onDeleteAction = (row: any) => {
    popup.open({
      title: 'Are you sure?',
      message: `Do you want to delete ${row?.name}? This cannot be undone.`,
      type: 'confirm',
      confirmButtonText: 'Delete',
      confirmButtonClass: twElementStyles.twButtonElegantRed,
      onAction: (confirm: boolean) => {
        if (confirm) {
          deleteTemplateType(
            { id: row.id },
            {
              onSuccess: () => {
                notification.push({
                  title: 'Deleted',
                  message: 'Template type deleted successfully.',
                  type: 'success',
                })
                refetch()
              },
              onError: (err) => notification.push(getErrorResponse(err)),
            }
          )

        }
      },
    })
  }

  /* ---------------- Actions ---------------- */
  const onAction = (actionType: string, row: any) => {
    if (actionType === 'edit') {
      setEditForm(true)
      //setAddUpdateInput(row)
      setAddUpdateInput({
        ...row,
        id: row.id || row.template_type_id,   // 🔥 ensure id always exists
      })
      setAddUpdate(true)
    }
    if (actionType === 'delete') {
      onDeleteAction(row)
    }
  }

  /* ---------------- Columns ---------------- */
  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'sr_no',
      header: () => 'ID',
      cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex
        const pageSize = table.getState().pagination.pageSize
        return pageIndex * pageSize + row.index + 1
      },
      enableSorting: false,
    },
    { accessorKey: 'name', header: 'NAME' },
    { accessorKey: 'code', header: 'CODE' },
    {
      header: 'WIDGET CODE',
      id: 'widget_code',
      cell: ({ row }) => {
        const widgets =
          row.original?.widgetsCodes?.widgets || []

        return widgets.length ? (
          <div className="flex flex-wrap gap-1">
            {widgets.map((code: string) => (
              <span
                key={code}
                className="px-2 py-0.5 text-[11px] rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
              >
                {code}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )
      },
    },

    { accessorKey: 'description', header: 'DESCRIPTION' },
    {
      header: () => 'STATUS',
      id: 'status',
      cell: ({ row }) =>
        RenderStatus(row, 'status', onStatusChange),
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      cell: ({ row }) =>
        RenderDefaultActions(row, ['edit'], onAction),
    },
  ]

  const useFormHook = useForm({ defaultValues: filterParams })
  const rowSelectionState = useState<RowSelectionState>({})

  useEffect(() => {
    const sub = useFormHook.watch((values, { name }) => {
      if (!name) return
      const updated = getUpdatedSearchParams(
        filterParams,
        values,
        name
      )
      if (Object.keys(updated).length) {
        setSearchParams({
          filter: encodeURIComponent(JSON.stringify(updated)),
        })
      } else {
        setSearchParams({})
      }
    })
    return () => sub.unsubscribe()
  }, [useFormHook, filterParams, setSearchParams])

  return isSuccess || isError ? (
    <SmoothTransition>
      {(error as any)?.response?.data?.statusCode === 403 ? (
        <Unauthorized />
      ) : (
        <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border shadow-md">
          <AddUpdate
            isEditForm={editForm}
            input_data={addUpdateInput}
            isOpen={addUpdate}
            setIsOpen={setAddUpdate}
            onSubmitHandler={() => {
              setAddUpdate(false)
              setAddUpdateInput(null)
              refetch()
            }}
          />

          <CustomTable
            title="Template Type Master"
            titleLabel={
              !isError
                ? `Last fetched on ${getTimeRelativeTime(
                  dataUpdatedAt
                )}`
                : ''
            }
            columns={columns}
            data={normalizedResults}
            pages={responseData?.pages}
            totalRecords={responseData?.total}
            isLoading={isFetching}
            isErrored={isError}
            onRefresh={refetch}
            // onAddAction={() => {
            //   setEditForm(false)
            //   setAddUpdateInput({})
            //   setAddUpdate(true)
            // }}
            onAddAction={undefined}   // 🔥 disables Create button
            rowSelectionState={rowSelectionState}
            formHook={useFormHook}
          />
        </div>
      )}
    </SmoothTransition>
  ) : (
    <PagePreloader />
  )
}

export default TemplateTypeMaster
